import { Bot } from 'lucide-react';
import { memo, useCallback } from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import { PATHS } from '@/constants/path';
import { useMyChatbots } from '@/features/admin/chatbots/hooks/useChatbots';
import type { MyChatbot } from '@/features/admin/chatbots/types/chatbot';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';

// Chỉ hiện ở "/" và "/dashboard" trần. Có query param (vd ?moduleId=...) là
// đã vào một ngữ cảnh cụ thể chứ không còn là trang chủ nữa → ẩn.

// Mở URL nguyên trạng, KHÔNG gắn ?login_hint=<email>. Hint chỉ giúp bỏ qua màn
// chọn tài khoản đúng một lần cho mỗi trình duyệt (sau đó cache MSAL của chatbot
// tự lo), không đáng để email nằm trong thanh địa chỉ và lịch sử duyệt web.
// Phía chatbot vẫn đọc `login_hint` nếu có, nên muốn bật lại chỉ cần sửa file này.

// Nút ghim cứng góc dưới phải MÀN HÌNH: 'fixed' + offset hằng số (right-16 = 56px,
// bottom-32 = 112px, vì html font-size 14px; ≥620px cao lên 130px; ≥700px lên 140px
// — bậc 620px là để zoom 90% (653px) không dùng chung giá trị với zoom 100% (588px).
// Máy đo thật: 1272×588 CSS px, dpr 1.5 (1920×1080 scale Windows 150%) —
// viewport chỉ 588px cao nên 140px đẩy nút lên tận 24-36% màn hình, đè nội dung.
// Riêng CỠ nút dùng clamp() để không
// tràn trên màn hình nhỏ.
//
// Vì sao MỘT giá trị cố định thay vì media query theo zoom: vật cản là AppFooter ở
// đáy trang chủ, cao ~110px tính theo CSS px (py-1 + pt-5/pb-4 + logo lg:h-20), và
// con số đó KHÔNG đổi theo zoom — zoom phóng cả trang đồng đều. Thứ đổi là footer
// có lọt vào màn hình hay không: viewport cao lên (zoom thấp) thì footer hiện ra và
// nút đè lên. 140px luôn hở ~30px trên footer, ở mọi mức zoom, mọi cỡ màn, mọi mức
// scale hệ điều hành.
//
// Bản trước bám theo min-width, đã bỏ vì hiệu chỉnh theo đúng một máy (1080p + scale
// Windows 150% → viewport 1272px ở zoom 100%). Máy 1080p KHÔNG scale cho viewport
// 1920px ngay ở zoom 100% nên rơi thẳng vào bậc cao nhất. Lưu lại để đối chiếu:
//   bottom-24 (84px)  | mặc định, width < 1350px
//   bottom-44 (154px) | min-[1350px]  ~ zoom 90%
//   bottom-62 (217px) | min-[1500px]  ~ zoom 80-70%
//   bottom-75 (262px) | min-[1850px]  ~ zoom 67% tro xuong

interface ChatbotIconProps {
  chatbot: MyChatbot;
  /** Một mình thì to như cũ; nhiều con thì thu lại cho vừa cột dọc. */
  solo: boolean;
  onOpen: (chatbot: MyChatbot) => void;
}

const ChatbotIcon = memo(function ChatbotIcon({
  chatbot,
  solo,
  onOpen,
}: ChatbotIconProps) {
  return (
    <div className="group relative flex items-center justify-end">
      {/* Tên hiện khi rê chuột, nằm bên trái icon */}
      <span
        className={cn(
          'bg-background text-foreground border-border pointer-events-none absolute right-full mr-3',
          'rounded-full border px-3 py-1.5 text-sm font-medium whitespace-nowrap',
          'opacity-0 shadow-[0_4px_12px_rgba(0,0,0,0.12)] transition-opacity duration-200',
          'group-hover:opacity-100',
        )}
      >
        {chatbot.chatbotName}
      </span>

      <button
        type="button"
        onClick={() => onOpen(chatbot)}
        aria-label={chatbot.chatbotName}
        title={chatbot.description || chatbot.chatbotName}
        className={cn(
          'cursor-pointer rounded-full transition-transform duration-300 hover:scale-110',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2',
          solo
            ? 'h-[clamp(4rem,12vmin,7.5rem)] w-[clamp(4rem,12vmin,7.5rem)]'
            : 'h-[clamp(3rem,8vmin,5rem)] w-[clamp(3rem,8vmin,5rem)]',
        )}
      >
        {chatbot.icon ? (
          <img
            src={chatbot.icon}
            alt={chatbot.chatbotName}
            className={cn(
              'h-full w-full rounded-full object-contain',
              'animate-[zoom-pulse_2s_ease-in-out_infinite]',
              'shadow-[0_6px_18px_rgba(0,0,0,0.15)]',
              '[filter:drop-shadow(0_4px_10px_rgba(0,0,0,0.25))]',
            )}
          />
        ) : (
          <span
            className={cn(
              'bg-primary text-primary-foreground flex h-full w-full items-center justify-center rounded-full',
              'animate-[zoom-pulse_2s_ease-in-out_infinite]',
              'shadow-[0_6px_18px_rgba(0,0,0,0.15)]',
            )}
          >
            <Bot className="h-2/5 w-2/5" />
          </span>
        )}
      </button>
    </div>
  );
});

export const ChatbotButton = memo(function ChatbotButton() {
  const rootMatch = useMatch(PATHS.ROOT);
  const dashboardMatch = useMatch(PATHS.DASHBOARD);
  const { search } = useLocation();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const onHomePath = !!rootMatch || !!dashboardMatch;
  const isHidden = !onHomePath || search !== '' || !isAuthenticated;

  const { data: chatbots } = useMyChatbots({ enabled: !isHidden });

  const handleOpen = useCallback((chatbot: MyChatbot) => {
    if (!chatbot.chatbotUrl) return;
    window.open(chatbot.chatbotUrl, '_blank', 'noopener,noreferrer');
  }, []);

  if (isHidden) return null;

  const items = (chatbots || []).filter(c => !!c.chatbotUrl);
  if (items.length === 0) return null;

  const solo = items.length === 1;

  return (
    <div className="fixed right-16 bottom-32 z-50 [@media(min-height:620px)]:bottom-[130px] [@media(min-height:700px)]:bottom-40 flex flex-col items-end gap-3">
      {items.map(chatbot => (
        <ChatbotIcon
          key={chatbot.id}
          chatbot={chatbot}
          solo={solo}
          onOpen={handleOpen}
        />
      ))}
    </div>
  );
});

export default ChatbotButton;
